import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../services/api';
import { eventService } from '../../services/eventService';
import { formatApiErrorMessage } from '../../utils/apiErrorMessage';
import { mergeSystemNotificationSettings } from '../../constants/notificationSettings';
import {
  fetchPoolSafetyAlertChannels,
  poolEffectiveSafetyChannels,
} from '../../services/poolSafetyAlertChannels';
import {
  SAFETY_ALERT_CHANNEL_LABELS,
  type SafetyAlertChannelOverrides,
} from '../../constants/poolSafetyAlertChannels';
import { DEFAULT_OPERATOR_ALERT_MESSAGE } from '../central-monitoring/centralRiskEventTypes';
import SafetyAlertImageAttachmentField from '../safety/SafetyAlertImageAttachmentField';
import { dataUrlToBase64 } from '../../utils/composeEventSnapshot';

type AlertChannel = 'sms' | 'kakao' | 'wearable';

export type PoolManualAlertTarget = {
  poolId: number;
  poolName: string;
  safetyPhoneRaw?: string;
};

type Props = {
  open: boolean;
  target: PoolManualAlertTarget | null;
  onClose: () => void;
  onSent?: () => void;
};

const PoolManualAlertDialog: React.FC<Props> = ({ open, target, onClose, onSent }) => {
  const [message, setMessage] = useState(DEFAULT_OPERATOR_ALERT_MESSAGE);
  const [phones, setPhones] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveDispatchHistory, setSaveDispatchHistory] = useState(true);
  const [channels, setChannels] = useState<SafetyAlertChannelOverrides>({
    sms: true,
    kakao: false,
    wearable: true,
  });
  const [poolChannelAllowed, setPoolChannelAllowed] = useState<SafetyAlertChannelOverrides>({
    sms: true,
    kakao: false,
    wearable: true,
  });
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [attachImageEnabled, setAttachImageEnabled] = useState(false);
  const [attachImageDataUrl, setAttachImageDataUrl] = useState<string | null>(null);
  const [attachImageFileName, setAttachImageFileName] = useState<string | null>(null);

  const loadChannelDefaults = useCallback(async (): Promise<SafetyAlertChannelOverrides> => {
    if (target?.poolId != null && Number.isFinite(Number(target.poolId))) {
      const ch = await fetchPoolSafetyAlertChannels(Number(target.poolId));
      const allowed = poolEffectiveSafetyChannels(ch);
      setPoolChannelAllowed(allowed);
      setChannels({ ...allowed });
      return allowed;
    }
    const response = await api.get<{ notifications?: Record<string, unknown> }>(
      '/api/settings/system'
    );
    const notifications = mergeSystemNotificationSettings(
      response?.notifications || undefined
    );
    const allowed = {
      sms: notifications.safety_channel_sms,
      kakao: notifications.safety_channel_kakao,
      wearable: notifications.safety_channel_wearable,
    };
    setPoolChannelAllowed(allowed);
    setChannels({ ...allowed });
    return allowed;
  }, [target?.poolId]);

  useEffect(() => {
    if (!open || !target) return;
    setMessage(DEFAULT_OPERATOR_ALERT_MESSAGE);
    setPhones((target.safetyPhoneRaw || '').trim());
    setError(null);
    setSaveDispatchHistory(true);
    setAttachImageEnabled(false);
    setAttachImageDataUrl(null);
    setAttachImageFileName(null);
    setChannelsLoading(true);
    void loadChannelDefaults()
      .catch(() => {
        const fallback = { sms: true, kakao: false, wearable: true };
        setPoolChannelAllowed(fallback);
        setChannels(fallback);
        setError('시설 알림 채널 설정을 불러오기 실패');
      })
      .finally(() => setChannelsLoading(false));
  }, [open, target, loadChannelDefaults]);

  useEffect(() => {
    if (!saveDispatchHistory) {
      setAttachImageEnabled(false);
      setAttachImageDataUrl(null);
      setAttachImageFileName(null);
    }
  }, [saveDispatchHistory]);

  const setChannelChecked = (key: AlertChannel, checked: boolean) => {
    if (!poolChannelAllowed[key]) return;
    setChannels((c) => ({ ...c, [key]: checked }));
  };

  const selectedChannels = useMemo(() => {
    const list: AlertChannel[] = [];
    if (poolChannelAllowed.sms && channels.sms) list.push('sms');
    if (poolChannelAllowed.kakao && channels.kakao) list.push('kakao');
    if (poolChannelAllowed.wearable && channels.wearable) list.push('wearable');
    return list;
  }, [channels, poolChannelAllowed]);

  const handleSend = async () => {
    if (!target) return;
    const text = message.trim();
    if (!text) {
      setError('메시지를 입력');
      return;
    }
    if (attachImageEnabled && !attachImageDataUrl) {
      setError('사진 함께 전송을 켰으면 이미지를 선택');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const phoneList = phones
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const snapshotBase64 =
        attachImageEnabled && attachImageDataUrl
          ? dataUrlToBase64(attachImageDataUrl) || undefined
          : undefined;
      await eventService.sendPoolSafetyAlert({
        poolId: target.poolId,
        message: text,
        phoneNumbers: phoneList.length > 0 ? phoneList : undefined,
        channels: selectedChannels,
        alertLevel: 'warning',
        recordEvent: false,
        operatorMessageOnly: true,
        recordDispatchLog: saveDispatchHistory,
        snapshotBase64,
        snapshotRawBase64: snapshotBase64,
      });
      onSent?.();
      onClose();
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '알림 전송에 실패'));
    } finally {
      setSending(false);
    }
  };

  if (!target) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 2000 }}
    >
      <DialogTitle>알림 메시지 — {target.poolName}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          SMS · 카카오 · 웨어러블 채널로만 전송. 관제·현장 모니터 팝업에는 표시되지 않으며,
          최근 이벤트 목록에도 남지 않음. 「발송 이력 저장」을 켠 경우에만 발송 이력에 남음.
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          전송 채널
        </Typography>
        {channelsLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">
              시설 채널 설정 불러오는 중…
            </Typography>
          </Box>
        ) : (
          <FormGroup row sx={{ mb: 1, gap: 1 }}>
            {(['sms', 'kakao', 'wearable'] as const).map((key) => {
              const allowed = poolChannelAllowed[key];
              const label = SAFETY_ALERT_CHANNEL_LABELS[key];
              return (
                <FormControlLabel
                  key={key}
                  disabled={!allowed}
                  control={
                    <Checkbox
                      checked={allowed ? channels[key] : false}
                      disabled={!allowed}
                      onChange={(e) => setChannelChecked(key, e.target.checked)}
                    />
                  }
                  label={allowed ? label : `${label} (시설 설정 OFF)`}
                />
              );
            })}
          </FormGroup>
        )}
        {((poolChannelAllowed.sms && channels.sms) ||
          (poolChannelAllowed.kakao && channels.kakao)) && (
          <TextField
            fullWidth
            label="수신 번호 (쉼표 구분, 비우면 시설 등록 번호)"
            value={phones}
            onChange={(e) => setPhones(e.target.value)}
            margin="normal"
            placeholder="01012345678, 01098765432"
          />
        )}
        <TextField
          fullWidth
          label="메시지"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          margin="normal"
          multiline
          minRows={4}
        />
        <SafetyAlertImageAttachmentField
          enabled={attachImageEnabled}
          onEnabledChange={(checked) => {
            setAttachImageEnabled(checked);
            if (!checked) {
              setAttachImageDataUrl(null);
              setAttachImageFileName(null);
            }
          }}
          previewDataUrl={attachImageDataUrl}
          fileName={attachImageFileName}
          onImageSelected={(dataUrl, name) => {
            setAttachImageDataUrl(dataUrl);
            setAttachImageFileName(name);
            setError(null);
          }}
          onClear={() => {
            setAttachImageDataUrl(null);
            setAttachImageFileName(null);
          }}
          onError={(msg) => setError(msg)}
          disabled={sending || !saveDispatchHistory}
          helperText={
            saveDispatchHistory
              ? '켜면 선택한 사진이 발송 이력에 함께 표시됨'
              : '발송 이력 저장을 켜야 사진을 이력에 남기기 가능'
          }
        />
        <FormControlLabel
          sx={{ mt: 1, alignItems: 'flex-start' }}
          control={
            <Switch
              checked={saveDispatchHistory}
              onChange={(_e, checked) => setSaveDispatchHistory(checked)}
            />
          }
          label={
            <Box>
              <Typography variant="body2">발송 이력 저장</Typography>
              <Typography variant="caption" color="text.secondary">
                켜면 현장 안전 알림 발송 이력에 기록됨. 끄면 전송만 하고 이력에는 남기지
                않음
              </Typography>
            </Box>
          }
        />
        {error ? (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          취소
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => void handleSend()}
          disabled={sending || channelsLoading}
          startIcon={sending ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          전송
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PoolManualAlertDialog;
